/**
 * Order types, mirroring the backend (`app/models/order.py`, `app/api/v1/orders/schemas.py`).
 */

/** The seven order states, in lifecycle order. */
export const ORDER_STATUSES = [
  'AWAITING_PAYMENT',
  'PENDING',
  'CONFIRMED',
  'PREPARING',
  'READY',
  'COMPLETED',
  'CANCELLED',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

export function isOrderStatus(value: string): value is OrderStatus {
  return (ORDER_STATUSES as readonly string[]).includes(value)
}

/** Human labels. Sentence case per the copy convention — never a raw enum in the UI. */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  AWAITING_PAYMENT: 'Awaiting payment',
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

/**
 * Semantic tone per status, resolved to design tokens by the badge.
 *
 * Assigned by *operational meaning*, not by position in the lifecycle: `READY` is the one
 * a person behind the counter scans for, so it gets the strongest positive treatment,
 * while `COMPLETED` recedes into neutral because it needs no action.
 */
export const ORDER_STATUS_TONE: Record<OrderStatus, 'neutral' | 'info' | 'accent' | 'success' | 'warning' | 'danger'> =
  {
    AWAITING_PAYMENT: 'warning',
    PENDING: 'warning',
    CONFIRMED: 'info',
    PREPARING: 'accent',
    READY: 'success',
    COMPLETED: 'neutral',
    CANCELLED: 'danger',
  }

/** `OrderItemResponse`. */
export type OrderItem = {
  id: number
  /**
   * Null once the product has been deleted.
   *
   * `OrderItem.product_id` is ON DELETE SET NULL server-side, because an order is a record
   * of what happened and has to survive the menu changing. So a line can point at nothing,
   * and anything reading this must cope — `itemName` falls back to the snapshot in
   * `customizations` and then to a plain label, never to `Product #null`.
   */
  product_id: number | null
  quantity: number
  unit_price: number
  redeem_quantity: number
  points_price: number | null
  points_discount_amount: number
  points_coverage: string | null
  customizations: Record<string, unknown> | null
}

/**
 * `OrderResponse` — mirrors `app/api/v1/orders/schemas.py:107` field for field.
 *
 * Money arrives as `float`, not a decimal string, so every amount here is a `number`.
 * Note `placed_at` rather than `created_at`: the list is ordered by it server-side, and
 * it is the timestamp staff mean when they say "when the order came in".
 */
export type Order = {
  id: number
  user_id: number | null
  /** Full name, falling back to email; composed server-side. Absent for guest orders. */
  user_name: string | null

  /** Money breakdown, all in dollars. */
  original_subtotal: number | null
  discount_amount: number | null
  subtotal: number
  tax_rate: number
  tax_amount: number
  tip_amount: number
  tip_percent: number | null
  total_amount: number
  points_spent: number
  points_refunded: boolean
  points_discount_amount: number

  /**
   * The part of the total paid from a wallet gift card, and how much of that has since
   * gone back to the card. The card was debited when the order was placed; Stripe charged
   * only `amount_due`. Optional because a backend before migration 0061 does not send them,
   * and an order without them is simply an order with no gift card part.
   */
  giftcard_amount?: number
  giftcard_refunded?: number
  /** The card's last four characters, for "Gift card ••••7Q2K". Null when none paid. */
  giftcard_last4?: string | null
  /** What was left for Stripe once the gift card had paid its part. */
  amount_due?: number

  status: OrderStatus
  placed_at: string
  updated_at: string | null

  pickup_code: string | null
  completed_at: string | null
  completed_by_user_id: number | null
  /** Staff ID of whoever completed it — shown so a shift can be reconstructed. */
  completed_by_staff_id: string | null

  cancelled_at: string | null
  cancelled_by_user_id: number | null
  cancellation_reason: string | null
  /**
   * What the customer asked the café to know — an allergy, a substitution, how to hand
   * it over. Order-level, because an allergy is a fact about the person rather than
   * about one drink.
   *
   * Never an empty string: the server trims it to `null`
   * (`OrderCreate.note_blank_is_no_note`), so `!== null` is the whole test for "there is
   * a note" and no caller has to decide whether blank counts.
   */
  note: string | null
  refund_status: 'initiated' | 'refunded' | null

  /** Set only while AWAITING_PAYMENT; the order auto-cancels after this. */
  payment_expires_at: string | null

  items: OrderItem[]
}

/** `PaginatedOrdersResponse` — note the field is `items`, not `orders`. */
export type PaginatedOrders = {
  items: Order[]
  total: number
}

/** The v1 cancellation reasons, verbatim (`index.html`, cancel-reason-select). */
export const CANCEL_REASONS = [
  'Customer request',
  'Out of stock / item unavailable',
  'Duplicate order',
  'Staff error',
  'Payment issue',
  'Other',
] as const

export type CancelReason = (typeof CANCEL_REASONS)[number]

/** `CancelOrderRequest.refund_mode`. */
export const REFUND_MODES = ['full', 'percentage', 'amount', 'none'] as const
export type RefundMode = (typeof REFUND_MODES)[number]
